import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { Checkbox, message } from "antd";

import { Button, DataTable, Input, SegmentedControl, StatusBadge } from "../../shared";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { formatString } from "../../../utils/constants/stringUtils";
import { formatUtcTimestampWithLocal } from "../../../queries/regulator-portal/compliance/formatUtcTimestamp";
import {
  approvalResultIssues,
  approvedImagesFromServices,
} from "../../../queries/regulator-portal/compliance/parseComplianceApproval";
import {
  filterPromotableServices,
  isPromotableServiceSelectable,
} from "../../../queries/regulator-portal/compliance/parsePromotableCatalog";
import { useApproveRunningServices } from "../../../queries/regulator-portal/compliance/useApproveRunningServices";
import { useApproveSelectedServices } from "../../../queries/regulator-portal/compliance/useApproveSelectedServices";
import { usePromotableServices } from "../../../queries/regulator-portal/compliance/usePromotableServices";
import type {
  PromotableService,
  PromotableServiceFilter,
} from "../../../queries/regulator-portal/compliance/types";
import ComplianceStatusBadge from "./ComplianceStatusBadge";
import { portalComplianceErrorMessage } from "./portalComplianceError";

import styles from "./Compliance.module.css";

const emptyValue = (value: string) => value || AppStrings.EMPTY_REASON;

const STATUS_TONE = {
  approved: "success",
  pending: "warning",
  unknown: "neutral",
} as const;

const RegulatorPortalServices: FC = () => {
  const catalogQuery = usePromotableServices();
  const approveRunning = useApproveRunningServices();
  const approveSelected = useApproveSelectedServices();
  const catalog = catalogQuery.data ?? null;
  const [statusFilter, setStatusFilter] = useState<PromotableServiceFilter>("all");
  const [search, setSearch] = useState("");
  const loadError =
    catalogQuery.isError && !catalog
      ? portalComplianceErrorMessage(
          catalogQuery.error,
          AppStrings.PAM_REGULATOR_PORTAL_SERVICES_LOAD_ERROR,
        )
      : null;
  const visibleServices = useMemo(
    () => filterPromotableServices(catalog?.services ?? [], statusFilter, search),
    [catalog?.services, statusFilter, search],
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const selectableVisibleIds = useMemo(
    () =>
      visibleServices
        .filter(isPromotableServiceSelectable)
        .map((service) => service.id),
    [visibleServices],
  );
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedVisibleCount = selectableVisibleIds.filter((id) =>
    selectedIdSet.has(id),
  ).length;

  useEffect(() => {
    const allowed = new Set(
      (catalog?.services ?? [])
        .filter(isPromotableServiceSelectable)
        .map((service) => service.id),
    );
    setSelectedIds((prev) => {
      const next = prev.filter((id) => allowed.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [catalog?.services]);

  const handleToggleAll = useCallback(
    (checked: boolean) => {
      setSelectedIds((prev) => {
        if (checked) {
          return Array.from(new Set([...prev, ...selectableVisibleIds]));
        }
        const dropping = new Set(selectableVisibleIds);
        return prev.filter((id) => !dropping.has(id));
      });
    },
    [selectableVisibleIds],
  );

  const handleToggleService = useCallback(
    (service: PromotableService, checked: boolean) => {
      if (!isPromotableServiceSelectable(service)) {
        return;
      }
      setSelectedIds((prev) => {
        if (checked) {
          return prev.includes(service.id) ? prev : [...prev, service.id];
        }
        return prev.filter((id) => id !== service.id);
      });
    },
    [],
  );

  const columns = useMemo(
    () => [
      {
        title: (
          <Checkbox
            checked={
              selectableVisibleIds.length > 0 &&
              selectedVisibleCount === selectableVisibleIds.length
            }
            indeterminate={
              selectedVisibleCount > 0 &&
              selectedVisibleCount < selectableVisibleIds.length
            }
            disabled={selectableVisibleIds.length === 0}
            onChange={(event) => handleToggleAll(event.target.checked)}
            aria-label={AppStrings.PAM_REGULATOR_PORTAL_SELECT_ALL_SERVICES}
            data-testid="regulator-portal-services-select-all"
          />
        ),
        key: "select",
        width: 48,
        ellipsis: false,
        className: styles.selectCell,
        render: (_value: unknown, record: PromotableService) => {
          const selectable = isPromotableServiceSelectable(record);
          return (
            <Checkbox
              checked={selectable && selectedIdSet.has(record.id)}
              disabled={!selectable}
              onChange={(event) =>
                handleToggleService(record, event.target.checked)
              }
              aria-label={formatString(
                AppStrings.PAM_REGULATOR_PORTAL_SELECT_SERVICE,
                { name: record.name || record.id },
              )}
              data-testid={`regulator-portal-services-select-${record.id}`}
            />
          );
        },
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_SERVICE_NAME,
        dataIndex: "name",
        key: "name",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.STATUS,
        dataIndex: "status",
        key: "status",
        render: (value: string) =>
          value ? (
            <ComplianceStatusBadge value={value} />
          ) : (
            AppStrings.EMPTY_REASON
          ),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_CURRENT_TAG,
        dataIndex: "currentTag",
        key: "currentTag",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_IMAGE_DIGEST,
        dataIndex: "imageDigest",
        key: "imageDigest",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_ECR_REPOSITORY,
        dataIndex: "ecrRepository",
        key: "ecrRepository",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_VALUES_FILE,
        dataIndex: "valuesFile",
        key: "valuesFile",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_REASON,
        dataIndex: "reason",
        key: "reason",
        render: (value: string) => emptyValue(value),
      },
    ],
    [
      handleToggleAll,
      handleToggleService,
      selectableVisibleIds,
      selectedIdSet,
      selectedVisibleCount,
    ],
  );

  const summary = catalog?.summary;
  const summaryChips = [
    { key: "total", label: AppStrings.TOTAL, value: summary?.total, tone: "neutral" as const },
    {
      key: "approved",
      label: AppStrings.PAM_REGULATOR_PORTAL_APPROVED,
      value: summary?.approved,
      tone: STATUS_TONE.approved,
    },
    {
      key: "pending",
      label: AppStrings.PAM_REGULATOR_PORTAL_PENDING,
      value: summary?.pending,
      tone: STATUS_TONE.pending,
    },
    {
      key: "unknown",
      label: AppStrings.PAM_REGULATOR_PORTAL_UNKNOWN,
      value: summary?.unknown,
      tone: STATUS_TONE.unknown,
    },
  ].filter((chip) => chip.value != null);

  const handleApproveRunning = useCallback(async () => {
    try {
      await approveRunning.mutateAsync();
      message.success(AppStrings.PAM_REGULATOR_PORTAL_APPROVE_RUNNING_SUCCESS);
    } catch (error) {
      message.error(
        portalComplianceErrorMessage(
          error,
          AppStrings.PAM_REGULATOR_PORTAL_APPROVE_RUNNING_ERROR,
        ),
      );
    }
  }, [approveRunning]);

  const handleApproveSelected = useCallback(async () => {
    const selectedServices = (catalog?.services ?? []).filter((service) =>
      selectedIdSet.has(service.id),
    );
    const approvedImages = approvedImagesFromServices(selectedServices);
    if (!approvedImages?.length) {
      message.error(AppStrings.PAM_REGULATOR_PORTAL_APPROVE_SELECTED_INVALID);
      return;
    }
    try {
      const result = await approveSelected.mutateAsync({ approvedImages });
      const issues = approvalResultIssues(result);
      if (issues.length > 0) {
        const details = formatString(
          AppStrings.PAM_REGULATOR_PORTAL_APPROVE_SELECTED_PARTIAL,
          { details: issues.join(", ") },
        );
        const hasUpdates =
          (result?.updatedServices.length ?? 0) > 0 ||
          (result?.updatedDbConfigs.length ?? 0) > 0;
        if (hasUpdates) {
          message.warning(details);
        } else {
          message.error(details);
        }
        return;
      }
      setSelectedIds([]);
      message.success(AppStrings.PAM_REGULATOR_PORTAL_APPROVE_SELECTED_SUCCESS);
    } catch (error) {
      message.error(
        portalComplianceErrorMessage(
          error,
          AppStrings.PAM_REGULATOR_PORTAL_APPROVE_SELECTED_ERROR,
        ),
      );
    }
  }, [approveSelected, catalog?.services, selectedIdSet]);

  return (
    <section className={styles.page} data-testid="regulator-portal-services">
      <div className={styles.header}>
        <h1 className={styles.title}>{AppStrings.PAM_REGULATOR_PORTAL_SERVICES}</h1>
        <div className={styles.headerActions}>
          {selectedIds.length > 0 ? (
            <Button
              onClick={handleApproveSelected}
              loading={approveSelected.isPending}
              disabled={approveRunning.isPending}
              data-testid="regulator-portal-approve-selected"
            >
              {AppStrings.PAM_REGULATOR_PORTAL_APPROVE_SELECTED}
            </Button>
          ) : null}
          <Button
            onClick={handleApproveRunning}
            loading={approveRunning.isPending}
            disabled={approveSelected.isPending}
            data-testid="regulator-portal-approve-running"
          >
            {AppStrings.PAM_REGULATOR_PORTAL_APPROVE_RUNNING}
          </Button>
        </div>
      </div>
      {catalog?.client || catalog?.regulator || catalog?.stage || catalog?.generatedAt ? (
        <div className={styles.headerMeta}>
          {catalog.client ? (
            <StatusBadge tone="neutral" size="sm">
              {AppStrings.PAM_REGULATOR_PORTAL_CLIENT}: {catalog.client}
            </StatusBadge>
          ) : null}
          {catalog.regulator ? (
            <StatusBadge tone="neutral" size="sm">
              {AppStrings.PAM_REGULATOR_PORTAL_REGULATOR_NAME}: {catalog.regulator}
            </StatusBadge>
          ) : null}
          {catalog.stage ? (
            <StatusBadge tone="neutral" size="sm">
              {AppStrings.PAM_REGULATOR_PORTAL_STAGE}: {catalog.stage}
            </StatusBadge>
          ) : null}
          {catalog.generatedAt ? (
            <StatusBadge tone="neutral" size="sm" data-testid="regulator-portal-services-generated">
              {AppStrings.PAM_REGULATOR_PORTAL_GENERATED_AT}:{" "}
              {formatUtcTimestampWithLocal(catalog.generatedAt)}
            </StatusBadge>
          ) : null}
        </div>
      ) : null}

      {loadError ? (
        <p className={styles.message} role="alert">
          {loadError}
        </p>
      ) : null}
      {catalogQuery.isLoading ? (
        <p className={styles.message}>{AppStrings.PAM_REGULATOR_PORTAL_LOADING}</p>
      ) : null}

      {!catalogQuery.isLoading && catalog ? (
        <>
          {summaryChips.length > 0 ? (
            <div className={styles.countsRow} data-testid="regulator-portal-services-summary">
              {summaryChips.map((chip) => (
                <StatusBadge key={chip.key} tone={chip.tone} size="sm">
                  {chip.label}: {chip.value}
                </StatusBadge>
              ))}
            </div>
          ) : null}

          <div className={styles.detailSectionHeader}>
            <SegmentedControl<PromotableServiceFilter>
              ariaLabel={AppStrings.PAM_REGULATOR_PORTAL_FILTER_SERVICES}
              data-testid="regulator-portal-services-filter"
              value={statusFilter}
              onChange={setStatusFilter}
              items={[
                { id: "all", label: AppStrings.PAM_REGULATOR_PORTAL_FILTER_ALL },
                { id: "approved", label: AppStrings.PAM_REGULATOR_PORTAL_APPROVED },
                { id: "pending", label: AppStrings.PAM_REGULATOR_PORTAL_PENDING },
                { id: "unknown", label: AppStrings.PAM_REGULATOR_PORTAL_UNKNOWN },
              ]}
            />
            <Input
              variant="search"
              className={styles.serviceSearch}
              placeholder={AppStrings.PAM_REGULATOR_PORTAL_SEARCH_SERVICES}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              allowClear
              data-testid="regulator-portal-services-search"
            />
          </div>

          {visibleServices.length > 0 ? (
            <div className={styles.tableSection}>
              <DataTable<PromotableService>
                rowKey="id"
                columns={columns}
                dataSource={visibleServices}
                pagination={false}
                scrollPreset="ultraWide"
                data-testid="regulator-portal-services-table"
              />
            </div>
          ) : (
            <p className={styles.message}>
              {catalog.services.length === 0 ||
              (statusFilter === "all" && !search.trim())
                ? AppStrings.PAM_REGULATOR_PORTAL_SERVICES_EMPTY
                : AppStrings.PAM_REGULATOR_PORTAL_FINDINGS_SEARCH_EMPTY}
            </p>
          )}
        </>
      ) : null}

      {!catalogQuery.isLoading && !loadError && !catalog ? (
        <p className={styles.message}>
          {AppStrings.PAM_REGULATOR_PORTAL_SERVICES_EMPTY}
        </p>
      ) : null}
    </section>
  );
};

export default RegulatorPortalServices;
